import type {
  ColDef,
  ColGroupDef,
  DefaultMenuItem,
  GetMainMenuItemsParams,
  KeyCreatorParams,
  MenuItemDef,
  ValueFormatterParams,
} from "ag-grid-community";

import { type components } from "../../api/openapi.ts";
import { CustomBooleanFilter } from "./CustomBooleanFilter.tsx";
import { NAVIGATION_COLUMN_ID } from "./frontend-columns.ts";
import { getValueByCellType } from "./get-value-by-cell-type.ts";
import { getWrapHeaderTextEnabled } from "./menu-items.ts";
import { RendererCellForType } from "./RendererCellForType.tsx";
import { DEFAULT_CELL_RENDERER_PARAMS } from "./settings.ts";

type ColumnResponse = components["schemas"]["ColumnResponse"];
type ExtraMainMenuItems = (params: GetMainMenuItemsParams) => (MenuItemDef | DefaultMenuItem)[];

function getFilterNameByFilterType(filterType: string) {
  switch (filterType) {
    case "text":
      return "agTextColumnFilter";
    case "number":
      return "agNumberColumnFilter";
    case "date":
      return "agDateColumnFilter";
    case "boolean":
      return CustomBooleanFilter;
    case "set":
      return "agSetColumnFilter";
    default:
      return "agTextColumnFilter";
  }
}

function getPlaceholderByFilterType(filterType: string) {
  switch (filterType) {
    case "text":
      return "Enter text...";
    case "number":
      return "Enter a number...";
    case "date":
      return false;
    case "boolean":
      return false;
    case "set":
      return false;
    default:
      return "Enter a filter...";
  }
}

const buildNavigationColDef = (): ColDef => {
  return {
    // Basic config
    headerName: "",
    colId: NAVIGATION_COLUMN_ID, // The fixed colId corresponding to the entity id
    field: NAVIGATION_COLUMN_ID,
    pinned: "left",
    lockPinned: true,
    suppressMovable: true,
    lockPosition: "left",
    resizable: false,
    width: 32,
    maxWidth: 32,
    headerStyle: { display: "flex", justifyContent: "center", alignItems: "center" },
    cellStyle: { display: "flex", justifyContent: "center", alignItems: "center" },

    suppressHeaderContextMenu: true,
    suppressHeaderMenuButton: true,

    contextMenuItems: [],

    // Rendering
    cellRendererParams: DEFAULT_CELL_RENDERER_PARAMS,

    // Sorting
    sortable: false,
  };
};

// Encapsulate leaf column construction
const buildLeafColDef = (node: ColumnResponse, extraMainMenuItems?: ExtraMainMenuItems): ColDef => {
  const { colId, headerName, field, fixed, cellType, filterOptions, sortable, filterable } = node;
  const { operators, values, filterType } = filterOptions;
  const valueList = Object.entries(values);

  return {
    // Basic config
    headerName,
    colId,
    field,
    tooltipComponent: "agTooltipComponent",
    headerTooltip: headerName,
    pinned: fixed ? "left" : undefined,
    lockPinned: fixed,
    suppressMovable: fixed,
    lockPosition: fixed ? "left" : undefined,

    // Data formatting (copy/paste, export, etc.)
    useValueFormatterForExport: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return getValueByCellType(cellType, params.value);
    },

    // Rendering
    cellRenderer: RendererCellForType({ type: cellType }),
    cellRendererParams: DEFAULT_CELL_RENDERER_PARAMS,
    // Sorting
    sortable,
    mainMenuItems: (params) => {
      const items: (MenuItemDef | DefaultMenuItem)[] = ["autoSizeThis", "autoSizeAll"];
      if (!fixed) {
        items.push("pinSubMenu");
      }
      const sortItems: (MenuItemDef | DefaultMenuItem)[] = ["sortAscending", "sortDescending", "sortUnSort"];
      const baseItems = sortable ? sortItems.concat(items) : items;
      const extraItems = extraMainMenuItems?.(params) ?? [];

      if (extraItems.length === 0) {
        return baseItems;
      }

      return baseItems.concat("separator", ...extraItems);
    },
    wrapHeaderText: getWrapHeaderTextEnabled(),
    autoHeaderHeight: getWrapHeaderTextEnabled(),

    filter: filterable ? getFilterNameByFilterType(valueList.length > 0 ? "set" : filterType) : false,
    filterParams: {
      buttons: ["reset", "apply"],
      closeOnApply: true,
      filterOptions: operators.map((op) => ({ ...op, predicate: true })), // "predicate: true" Why? I don't know but it works.
      maxNumConditions: 1,
      suppressAndOrCondition: true,
      values: valueList,
      keyCreator: (params: KeyCreatorParams) => params.value[0],
      valueFormatter: (params: Record<string, string>) => params.value[1],
      defaultToNothingSelected: true,
      filterPlaceholder: getPlaceholderByFilterType(filterType),
    },
  };
};

// Recursively build column defs; groups only carry group-related props
const buildColDef = (node: ColumnResponse, extraMainMenuItems?: ExtraMainMenuItems): ColDef | ColGroupDef => {
  const { headerName, children } = node;

  if (children && children.length) {
    return {
      headerName,
      wrapHeaderText: getWrapHeaderTextEnabled(),
      autoHeaderHeight: getWrapHeaderTextEnabled(),
      children: children.map((child) => buildColDef(child, extraMainMenuItems)),
    };
  }

  return buildLeafColDef(node, extraMainMenuItems);
};

export function mergeSplitGroups(
  columns: ColumnResponse[],
  sortModels: components["schemas"]["SortModel"][] | undefined,
  options?: {
    extraMainMenuItems?: ExtraMainMenuItems;
  },
): {
  colDefs: ColDef[];
  expandedLeafOrder: string[]; // for applyColumnState
} {
  const build: (c: ColumnResponse) => ColDef = (column) => buildColDef(column, options?.extraMainMenuItems);
  const groupMap = new Map<string, { group: ColGroupDef; childOrder: string[]; childSeen: Set<string> }>();
  const leafMap = new Map<string, ColDef>();
  const topLevelOrder: string[] = []; // order of first appearance (groupId/leafId)

  const ensureTopLevelKey = (key: string) => {
    if (!topLevelOrder.includes(key)) topLevelOrder.push(key);
  };

  for (const entry of columns) {
    const hasChildren = !!entry.children?.length;

    if (hasChildren) {
      // Top-level group chunk
      const groupId = entry.colId;
      if (!groupId) throw new Error("Group chunk missing colId");

      let rec = groupMap.get(groupId);
      if (!rec) {
        rec = {
          group: {
            groupId,
            headerName: entry.headerName ?? groupId,
            wrapHeaderText: getWrapHeaderTextEnabled(),
            autoHeaderHeight: getWrapHeaderTextEnabled(),
            children: [],
          },
          childOrder: [],
          childSeen: new Set<string>(),
        };
        groupMap.set(groupId, rec);
        ensureTopLevelKey(groupId);
      }

      for (const child of entry.children!) {
        const cid = child.colId || child.field;
        if (!cid) throw new Error(`Child missing colId/field under group ${groupId}`);
        if (!rec.childSeen.has(cid)) {
          const childDef = build(child);
          // Ensure the childDef has colId for later column state
          if (!childDef.colId) childDef.colId = cid;

          // Apply sort model if provided
          const sortModel = sortModels?.find((sortModel) => sortModel.colId === childDef.colId);
          if (sortModel?.colId && sortModel?.sort) {
            childDef.sort = sortModel.sort;
          }

          (rec.group.children as ColDef[]).push(childDef);
          rec.childSeen.add(cid);
          rec.childOrder.push(cid);
        }
      }
    } else {
      // Standalone top-level leaf
      const leafId = entry.colId || entry.field;
      if (!leafId) throw new Error("Leaf column missing colId/field");

      if (!leafMap.has(leafId)) {
        const def = build(entry);
        if (!def.colId) def.colId = leafId;

        // Apply sort model if provided
        const sortModel = sortModels?.find((sortModel) => sortModel.colId === def.colId);
        if (sortModel?.colId && sortModel?.sort) {
          def.sort = sortModel.sort;
        }

        leafMap.set(leafId, def);
        ensureTopLevelKey(leafId);
      }
    }
  }

  // Build top-level defs in first-appearance order
  const colDefs = [
    ...topLevelOrder.map((key) => {
      return groupMap.get(key)?.group ?? (leafMap.get(key) as ColDef);
    }),
    buildNavigationColDef(),
  ];

  // Expanded leaf order: each top-level item expands into its child leaf IDs (or itself if leaf)
  const expandedLeafOrder: string[] = columns
    .flatMap((col) =>
      col.children && col.children.length > 0 ? col.children.map((child) => child.colId) : [col.colId],
    )
    .concat([NAVIGATION_COLUMN_ID]);

  return { colDefs, expandedLeafOrder };
}
